import { Box, Tabs, Text } from "@mantine/core"
import PlayerSearchForm from "../FormComponents/PlayerSearchForm"
import { IconBallBasketball, IconUser } from "@tabler/icons-react"
import GameSearchForm from "../FormComponents/GameSearchForm"
import { GameSearchData, PlayerSearchData } from "../../models/SearchData"
import { useEffect, useMemo, useState, useCallback } from "react"
import { MAX_STACK_SIZE, StackedQuery } from "../../models/QueryStack"
import { QueryStack } from "../QueryStack"

interface SearchFilterProps {
  playerSearchData: PlayerSearchData | null
  gameSearchData: GameSearchData | null
  activeSearchType?: "player" | "game" | null
  handleOnPlayerSearch: (data: PlayerSearchData) => void
  handleOnGameSearch: (data: GameSearchData) => void
  handleOnNewPlayerSearch?: (data: PlayerSearchData) => void
  handleOnNewGameSearch?: (data: GameSearchData) => void
  stackedQueries?: StackedQuery[]
  editingQueryId?: string | null
  isMultiQuery?: boolean
  onSelectQuery?: (query: StackedQuery) => void
  onAddToStackPlayer?: (data: PlayerSearchData) => void
  onAddToStackGame?: (data: GameSearchData) => void
  onUpdateInStackPlayer?: (data: PlayerSearchData) => void
  onUpdateInStackGame?: (data: GameSearchData) => void
  onCancelEdit?: () => void
  onRemoveFromStack?: (id: string) => void
  onClearStack?: () => void
  onExecuteStack?: () => void
  isStackLoading?: boolean
  activeTab?: string | null
  onTabChange?: (tab: string | null) => void
}

const SearchFilter: React.FC<SearchFilterProps> = ({
  playerSearchData,
  gameSearchData,
  activeSearchType,
  handleOnPlayerSearch,
  handleOnGameSearch,
  handleOnNewPlayerSearch,
  handleOnNewGameSearch,
  stackedQueries,
  editingQueryId,
  isMultiQuery = false,
  onSelectQuery,
  onAddToStackPlayer,
  onAddToStackGame,
  onUpdateInStackPlayer,
  onUpdateInStackGame,
  onCancelEdit,
  onRemoveFromStack,
  onClearStack,
  onExecuteStack,
  isStackLoading = false,
  activeTab: controlledActiveTab,
  onTabChange,
}) => {
  const editingQuery = useMemo(() => {
    if (!editingQueryId || !stackedQueries) return null
    return stackedQueries.find((q) => q.id === editingQueryId) || null
  }, [editingQueryId, stackedQueries])

  const editingPlayerData = useMemo<PlayerSearchData | null>(() => {
    if (!editingQuery || editingQuery.type !== "player") return null
    return editingQuery
  }, [editingQuery])

  const editingGameData = useMemo<GameSearchData | null>(() => {
    if (!editingQuery || editingQuery.type !== "game") return null
    return editingQuery
  }, [editingQuery])

  const [internalActiveTab, setInternalActiveTab] = useState<string | null>(
    activeSearchType === "game" || gameSearchData ? "games" : "players"
  )

  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab

  const handleTabChange = useCallback((tab: string | null) => {
    setInternalActiveTab(tab)
    onTabChange?.(tab)
  }, [onTabChange])

  useEffect(() => {
    if (editingQuery) {
      if (editingQuery.type === "player") {
        handleTabChange("players")
      } else if (editingQuery.type === "game") {
        handleTabChange("games")
      }
    } else if (activeSearchType === "game" || gameSearchData) {
      handleTabChange("games")
    } else if (activeSearchType === "player" || playerSearchData) {
      handleTabChange("players")
    }
  }, [editingQuery, activeSearchType, gameSearchData, playerSearchData, handleTabChange])

  const isStackMode = Boolean(isMultiQuery || (stackedQueries && stackedQueries.length > 0))
  const isStackFull = Boolean(stackedQueries && stackedQueries.length >= MAX_STACK_SIZE)
  const isEditingPlayer = Boolean(editingQuery && editingQuery.type === "player")
  const isEditingGame = Boolean(editingQuery && editingQuery.type === "game")

  return (
    <Box p={5}>
      {isStackMode && stackedQueries && stackedQueries.length > 0 && (
        <QueryStack
          queries={stackedQueries}
          selectedQueryId={editingQueryId}
          onSelectQuery={onSelectQuery}
          onRemove={onRemoveFromStack || (() => {})}
          onClearAll={onClearStack || (() => {})}
          onExecute={onExecuteStack || (() => {})}
          isLoading={isStackLoading}
        />
      )}

      <Tabs keepMounted={false} value={activeTab} onChange={handleTabChange}>
        <Tabs.List w='100%'>
          <Tabs.Tab value="players" w="50%" leftSection={<IconUser />}>
            <Text>Players</Text>
          </Tabs.Tab>
          <Tabs.Tab value="games" w="50%" leftSection={<IconBallBasketball />}>
            <Text>Games</Text>
          </Tabs.Tab>
        </Tabs.List>
      
        <Tabs.Panel value="players">
          <PlayerSearchForm
            submitText={"Search"}
            lastSearch={isEditingPlayer ? editingPlayerData : playerSearchData}
            onSubmit={handleOnPlayerSearch}
            onNewSearch={handleOnNewPlayerSearch || handleOnPlayerSearch}
            onAddToStack={onAddToStackPlayer}
            isStackMode={isStackMode}
            isStackFull={isStackFull}
            isEditing={isEditingPlayer}
            onUpdateInStack={onUpdateInStackPlayer}
            onCancelEdit={onCancelEdit}
          />
        </Tabs.Panel>
        <Tabs.Panel value="games">
          <GameSearchForm
            submitText={"Get clips"}
            lastSearch={isEditingGame ? editingGameData : gameSearchData}
            onSubmit={handleOnGameSearch}
            onNewSearch={handleOnNewGameSearch || handleOnGameSearch}
            onAddToStack={onAddToStackGame}
            isStackMode={isStackMode}
            isStackFull={isStackFull}
            isEditing={isEditingGame}
            onUpdateInStack={onUpdateInStackGame}
            onCancelEdit={onCancelEdit}
          />
        </Tabs.Panel>
      </Tabs>
    </Box>
  )
}

export default SearchFilter